// Define FeatureValueType locally to avoid circular dependency
export enum FeatureValueType {
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  STRING = 'string',
  JSON = 'json',
}

// Feature keys used in subscription plans - MVP version
export const FEATURE_KEYS = {
  // Limits
  MAX_USERS: 'max_users',
  MAX_PROJECTS: 'max_projects',
  MAX_CUSTOM_FIELDS: 'max_custom_fields',
  MAX_TEMPLATES: 'max_templates',
  MAX_FORMS: 'max_forms',
  MAX_COLLECTIONS: 'max_collections',
  MAX_LEADS: 'max_leads',
  MAX_DEALS: 'max_deals',
  MAX_CONTACTS: 'max_contacts',
  MAX_ENTITIES: 'max_entities',
  STORAGE_GB: 'storage_gb',
  MARKETING_HOURLY_SENDS: 'marketing_hourly_sends',
  MARKETING_DAILY_SENDS: 'marketing_daily_sends',

  // AI Features
  AI_CREDITS_MONTHLY: 'ai_credits_monthly',

  // Core Features
  PROJECTS: 'projects',
  TASKS_UNLIMITED: 'tasks_unlimited',
  TICKETS: 'tickets',
  TIME_TRACKING: 'time_tracking',
  DISCUSSIONS: 'discussions',
  FORMS: 'forms',

  // Entity Management
  ENTITY_PORTAL: 'client_portal',
  LEADS: 'leads',
  DEALS: 'deals',

  // Business Features
  EVENTS: 'events',
  DIRECTORY: 'directory',
  TEAM: 'team',
  INVENTORY: 'inventory',
  SUPPLIERS: 'suppliers',
  CONTRACTS: 'contracts',

  // Advanced Features
  ADVANCED_REPORTS: 'advanced_reports',
  API_ACCESS: 'api_access',
  WORKFLOWS: 'workflows',
  ANALYTICS: 'analytics',
  COLLECTIONS: 'collections',
  INTEGRATIONS: 'integrations',
  DATA_EXPORT: 'data_export',
  REMOVE_WATERMARK: 'remove_watermark',
  FORM_CAPTCHA: 'form_captcha',
  FORM_KIOSK_MODE: 'form_kiosk_mode',

  // Support
  EMAIL_SUPPORT: 'email_support',
  PRIORITY_SUPPORT: 'priority_support',
} as const;

export type FeatureKey = typeof FEATURE_KEYS[keyof typeof FEATURE_KEYS];

// Feature definitions for seeding
export const SUBSCRIPTION_FEATURES = [
  { 
    key: FEATURE_KEYS.MAX_USERS, 
    name: 'Max Users', 
    description: 'Maximum number of team members', 
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 3
  },
  { 
    key: FEATURE_KEYS.MAX_PROJECTS, 
    name: 'Max Projects', 
    description: 'Maximum number of active projects', 
    category: 'collaboration', 
    valueType: FeatureValueType.NUMBER,
    defaultValue: 2
  },
  {
    key: FEATURE_KEYS.STORAGE_GB,
    name: 'Storage GB',
    description: 'Storage space in gigabytes',
    category: 'storage',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 1
  },
  {
    key: FEATURE_KEYS.MARKETING_HOURLY_SENDS,
    name: 'Marketing Hourly Sends',
    description: 'Maximum marketing messages sent per organization per hour',
    category: 'marketing',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 50
  },
  {
    key: FEATURE_KEYS.MARKETING_DAILY_SENDS,
    name: 'Marketing Daily Sends',
    description: 'Maximum marketing messages sent per organization per day',
    category: 'marketing',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 100
  },
  {
    key: FEATURE_KEYS.MAX_CUSTOM_FIELDS,
    name: 'Max Custom Fields',
    description: 'Maximum number of custom fields per module',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 3
  },
  {
    key: FEATURE_KEYS.MAX_TEMPLATES,
    name: 'Max Templates',
    description: 'Maximum number of project templates',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 1
  },
  {
    key: FEATURE_KEYS.MAX_FORMS,
    name: 'Max Forms',
    description: 'Maximum number of forms',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 2
  },
  {
    key: FEATURE_KEYS.MAX_COLLECTIONS,
    name: 'Max Collections',
    description: 'Maximum number of custom collections',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 10
  },
  {
    key: FEATURE_KEYS.MAX_LEADS,
    name: 'Legacy CRM Record Limit',
    description: 'Legacy CRM record limit retained for migration compatibility',
    category: 'sales',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 50
  },
  {
    key: FEATURE_KEYS.MAX_DEALS,
    name: 'Max Deals',
    description: 'Maximum number of deals',
    category: 'sales',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 50
  },
  {
    key: FEATURE_KEYS.MAX_CONTACTS,
    name: 'Max Contacts',
    description: 'Maximum number of contacts',
    category: 'sales',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 25000
  },
  {
    key: FEATURE_KEYS.MAX_ENTITIES,
    name: 'Max Entities',
    description: 'Maximum number of entities (clients/companies)',
    category: 'sales',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 50
  },
  {
    key: FEATURE_KEYS.AI_CREDITS_MONTHLY, 
    name: 'AI Credits Monthly', 
    description: 'Monthly AI credits for AI-powered features', 
    category: 'ai', 
    valueType: FeatureValueType.NUMBER,
    defaultValue: 0
  },
  { 
    key: FEATURE_KEYS.PROJECTS, 
    name: 'Projects', 
    description: 'Access to project management features', 
    category: 'collaboration', 
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true
  },
  { 
    key: FEATURE_KEYS.TASKS_UNLIMITED, 
    name: 'Tasks Unlimited', 
    description: 'Unlimited tasks across all projects', 
    category: 'collaboration', 
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true
  },
  {
    key: FEATURE_KEYS.TICKETS,
    name: 'Tickets',
    description: 'Help desk ticketing and request tracking',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true
  },
  { 
    key: FEATURE_KEYS.TIME_TRACKING, 
    name: 'Time Tracking', 
    description: 'Track time on tasks and projects', 
    category: 'analytics', 
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true
  },
  {
    key: FEATURE_KEYS.DISCUSSIONS,
    name: 'Discussions',
    description: 'Team discussions and comments',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true
  },
  {
    key: FEATURE_KEYS.FORMS,
    name: 'Forms',
    description: 'Build and publish forms to collect submissions',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.ENTITY_PORTAL,
    name: 'Entity Portal', 
    description: 'Dedicated portal for client access', 
    category: 'collaboration', 
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.LEADS,
    name: 'Legacy CRM Access',
    description: 'Legacy CRM access retained for migration compatibility',
    category: 'sales',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  { 
    key: FEATURE_KEYS.DEALS, 
    name: 'Deals Management', 
    description: 'Manage sales deals and pipeline', 
    category: 'sales', 
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  { 
    key: FEATURE_KEYS.EVENTS, 
    name: 'Events', 
    description: 'Event management and scheduling', 
    category: 'business', 
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  { 
    key: FEATURE_KEYS.DIRECTORY, 
    name: 'Directory', 
    description: 'Team and contact directory', 
    category: 'business', 
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.TEAM,
    name: 'Team',
    description: 'Team management, scheduling, and resource allocation',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.INVENTORY,
    name: 'Inventory',
    description: 'Inventory management and tracking',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.SUPPLIERS,
    name: 'Suppliers',
    description: 'Supplier and vendor management',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.CONTRACTS,
    name: 'Contracts',
    description: 'Contract creation and tracking',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.ADVANCED_REPORTS,
    name: 'Advanced Reports', 
    description: 'Advanced analytics and reporting', 
    category: 'analytics', 
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.API_ACCESS,
    name: 'API Access',
    description: 'API access for integrations',
    category: 'integration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.WORKFLOWS,
    name: 'Workflows',
    description: 'Automated workflows and triggers',
    category: 'automation',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.ANALYTICS,
    name: 'Analytics',
    description: 'Analytics dashboards and insights',
    category: 'analytics',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.COLLECTIONS,
    name: 'Collections',
    description: 'Organize items into collections',
    category: 'organization',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.INTEGRATIONS,
    name: 'Integrations',
    description: 'Connect third-party services like Gmail, Outlook, and Google Calendar',
    category: 'integration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.DATA_EXPORT,
    name: 'Data Export',
    description: 'Export data to CSV, Excel, and other formats',
    category: 'integration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.REMOVE_WATERMARK,
    name: 'Remove Watermark',
    description: 'Remove Scrambl branding from invoices and forms',
    category: 'branding',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.FORM_CAPTCHA,
    name: 'Form CAPTCHA',
    description: 'Enable reCAPTCHA spam protection on forms',
    category: 'security',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.FORM_KIOSK_MODE,
    name: 'Form Kiosk Mode',
    description: 'Allow shared-device form submissions (bypasses CAPTCHA)',
    category: 'security',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.EMAIL_SUPPORT,
    name: 'Email Support',
    description: 'Email support',
    category: 'support',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  },
  {
    key: FEATURE_KEYS.PRIORITY_SUPPORT,
    name: 'Priority Support',
    description: 'Priority customer support',
    category: 'support',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false
  }
] as const;

// Subscription plan configuration interface
export interface SubscriptionPlanConfig {
  key: string;
  name: string;
  displayName: string;
  description: string;
  price: number; // Monthly price
  annualPrice: number; // Annual price
  pricePerUser: number; // Per-user pricing
  trialPeriodDays: number;
  sortOrder: number;
  stripeProductId: string;
  stripePriceId: string; // Monthly Stripe price ID
  stripeAnnualPriceId: string; // Annual Stripe price ID
  isComingSoon?: boolean; // Flag to indicate if plan is coming soon
  features: Record<FeatureKey, boolean | number>;
}

// Helper function to get environment variable or return default
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue;
  }
  return defaultValue;
};

// Complete subscription plan configurations - Single source of truth
export const SUBSCRIPTION_PLAN_CONFIGS: SubscriptionPlanConfig[] = [
  {
    key: 'free',
    name: 'free',
    displayName: 'Free',
    description: 'Perfect for individuals',
    price: 0,
    annualPrice: 0,
    pricePerUser: 0, // $0 per user (free)
    trialPeriodDays: 0,
    sortOrder: 1,
    stripeProductId: getEnvVar('STRIPE_FREE_PRODUCT_ID', 'prod_SSMpS0MlwxtOi6'),
    stripePriceId: getEnvVar('STRIPE_FREE_PRICE_ID', 'price_1RXS6ER0phIDD6nqyhzekQva'),
    stripeAnnualPriceId: getEnvVar('STRIPE_FREE_ANNUAL_PRICE_ID', 'price_1RXS6ER0phIDD6nqyhzekQva'),
    features: {
      // Limits
      [FEATURE_KEYS.MAX_USERS]: 10, // Allow up to 10 users (seats) on free plan
      [FEATURE_KEYS.MAX_PROJECTS]: 2,
      [FEATURE_KEYS.MAX_CUSTOM_FIELDS]: 10, // Limited to 10 custom fields per module
      [FEATURE_KEYS.MAX_TEMPLATES]: 1, // Limited to 1 project template
      [FEATURE_KEYS.MAX_FORMS]: 2, // Limited to 2 forms (public only)
      [FEATURE_KEYS.MAX_COLLECTIONS]: 10, // Limited to 10 collections
      [FEATURE_KEYS.MAX_LEADS]: 50, // Legacy migration limit
      [FEATURE_KEYS.MAX_DEALS]: 50, // Limited to 50 deals
      [FEATURE_KEYS.MAX_CONTACTS]: 25000, // Limited to 25,000 contacts
      [FEATURE_KEYS.MAX_ENTITIES]: 50, // Limited to 50 entities
      [FEATURE_KEYS.STORAGE_GB]: 1,
      [FEATURE_KEYS.MARKETING_HOURLY_SENDS]: 50,
      [FEATURE_KEYS.MARKETING_DAILY_SENDS]: 100,

      // AI Features
      [FEATURE_KEYS.AI_CREDITS_MONTHLY]: 50,

      // Core Features
      [FEATURE_KEYS.PROJECTS]: true,
      [FEATURE_KEYS.TASKS_UNLIMITED]: true,
      [FEATURE_KEYS.TICKETS]: false, // Not in free tier
      [FEATURE_KEYS.TIME_TRACKING]: true,
      [FEATURE_KEYS.DISCUSSIONS]: true, // Enabled for all plans - core collaboration feature
      [FEATURE_KEYS.FORMS]: false, // Not in free tier

      // Entity Management
      [FEATURE_KEYS.ENTITY_PORTAL]: false,
      [FEATURE_KEYS.LEADS]: false, // Not in free tier
      [FEATURE_KEYS.DEALS]: false, // Not in free tier

      // Business Features
      [FEATURE_KEYS.EVENTS]: false,
      [FEATURE_KEYS.DIRECTORY]: false,
      [FEATURE_KEYS.TEAM]: false, // Not in free tier
      [FEATURE_KEYS.INVENTORY]: true,
      [FEATURE_KEYS.SUPPLIERS]: false, // Not in free tier
      [FEATURE_KEYS.CONTRACTS]: false, // Not in free tier

      // Advanced Features
      [FEATURE_KEYS.ADVANCED_REPORTS]: false,
      [FEATURE_KEYS.API_ACCESS]: false,
      [FEATURE_KEYS.WORKFLOWS]: false, // Not in free tier
      [FEATURE_KEYS.ANALYTICS]: false, // Not in free tier
      [FEATURE_KEYS.COLLECTIONS]: false, // Not in free tier
      [FEATURE_KEYS.INTEGRATIONS]: false, // Not in free tier
      [FEATURE_KEYS.DATA_EXPORT]: false, // No CSV/Excel export in free tier
      [FEATURE_KEYS.REMOVE_WATERMARK]: false, // Shows "Powered by Scrambl" watermark
      [FEATURE_KEYS.FORM_CAPTCHA]: false, // No reCAPTCHA on free tier
      [FEATURE_KEYS.FORM_KIOSK_MODE]: false, // No kiosk mode on free tier

      // Support
      [FEATURE_KEYS.EMAIL_SUPPORT]: false,
      [FEATURE_KEYS.PRIORITY_SUPPORT]: false,
    },
  },
  {
    key: 'pro',
    name: 'pro',
    displayName: 'Pro',
    description: 'For growing teams that need more',
    price: 12, // $12/month when billed monthly
    annualPrice: 120, // $120/year ($10/month when billed annually)
    pricePerUser: 12,
    trialPeriodDays: 30,
    sortOrder: 2,
    stripeProductId: getEnvVar('STRIPE_PRO_PRODUCT_ID', 'prod_SSPfcQxYAsoJi5'),
    stripePriceId: getEnvVar('STRIPE_PRO_PRICE_ID', 'price_1T3p6PJpDoPtvtpeCX0gfD8s'),
    stripeAnnualPriceId: getEnvVar('STRIPE_PRO_ANNUAL_PRICE_ID', 'price_1T3p6qJpDoPtvtpeoIxtg0pW'),
    features: {
      // Limits
      [FEATURE_KEYS.MAX_USERS]: 50, // Allow up to 50 users on pro plan
      [FEATURE_KEYS.MAX_PROJECTS]: -1, // Unlimited projects
      [FEATURE_KEYS.MAX_CUSTOM_FIELDS]: -1, // Unlimited custom fields
      [FEATURE_KEYS.MAX_TEMPLATES]: -1, // Unlimited templates
      [FEATURE_KEYS.MAX_FORMS]: -1, // Unlimited forms
      [FEATURE_KEYS.MAX_COLLECTIONS]: -1, // Unlimited collections
      [FEATURE_KEYS.MAX_LEADS]: -1, // Legacy migration limit
      [FEATURE_KEYS.MAX_DEALS]: -1, // Unlimited deals
      [FEATURE_KEYS.MAX_CONTACTS]: -1, // Unlimited contacts
      [FEATURE_KEYS.MAX_ENTITIES]: -1, // Unlimited entities
      [FEATURE_KEYS.STORAGE_GB]: 100,
      [FEATURE_KEYS.MARKETING_HOURLY_SENDS]: 1000,
      [FEATURE_KEYS.MARKETING_DAILY_SENDS]: 10000,

      // AI Features
      [FEATURE_KEYS.AI_CREDITS_MONTHLY]: 200,

      // Core Features
      [FEATURE_KEYS.PROJECTS]: true,
      [FEATURE_KEYS.TASKS_UNLIMITED]: true,
      [FEATURE_KEYS.TICKETS]: true,
      [FEATURE_KEYS.TIME_TRACKING]: true,
      [FEATURE_KEYS.DISCUSSIONS]: true,
      [FEATURE_KEYS.FORMS]: true, // Included in pro

      // Entity Management
      [FEATURE_KEYS.ENTITY_PORTAL]: true,
      [FEATURE_KEYS.LEADS]: true,
      [FEATURE_KEYS.DEALS]: true,

      // Business Features
      [FEATURE_KEYS.EVENTS]: true,
      [FEATURE_KEYS.DIRECTORY]: false,
      [FEATURE_KEYS.TEAM]: true, // Included in pro
      [FEATURE_KEYS.INVENTORY]: true,
      [FEATURE_KEYS.SUPPLIERS]: true, // Included in pro
      [FEATURE_KEYS.CONTRACTS]: true, // Included in pro

      // Advanced Features
      [FEATURE_KEYS.ADVANCED_REPORTS]: true,
      [FEATURE_KEYS.API_ACCESS]: false,
      [FEATURE_KEYS.WORKFLOWS]: true, // Included in pro
      [FEATURE_KEYS.ANALYTICS]: false, // Business tier only
      [FEATURE_KEYS.COLLECTIONS]: true, // Included in pro
      [FEATURE_KEYS.INTEGRATIONS]: true, // Included in pro
      [FEATURE_KEYS.DATA_EXPORT]: true, // CSV/Excel export included
      [FEATURE_KEYS.REMOVE_WATERMARK]: true, // No watermark on invoices/forms
      [FEATURE_KEYS.FORM_CAPTCHA]: true, // reCAPTCHA enabled for pro
      [FEATURE_KEYS.FORM_KIOSK_MODE]: true, // Kiosk mode enabled for pro

      // Support
      [FEATURE_KEYS.EMAIL_SUPPORT]: true,
      [FEATURE_KEYS.PRIORITY_SUPPORT]: false,
    },
  },
  {
    key: 'business',
    name: 'business',
    displayName: 'Business',
    description: 'For teams that need it all',
    price: 12, // $12/month when billed monthly
    annualPrice: 120, // $120/year ($10/month when billed annually)
    pricePerUser: 12,
    trialPeriodDays: 30,
    sortOrder: 3,
    stripeProductId: getEnvVar('STRIPE_BUSINESS_PRODUCT_ID', 'prod_SSPiUj9uV4dxu8'),
    stripePriceId: getEnvVar('STRIPE_BUSINESS_PRICE_ID', 'price_1RXUtAR0phIDD6nqBvdRCOVv'),
    stripeAnnualPriceId: getEnvVar('STRIPE_BUSINESS_ANNUAL_PRICE_ID', 'price_1RYa16R0phIDD6nqtw8YPRQt'),
    isComingSoon: true,
    features: {
      // Limits
      [FEATURE_KEYS.MAX_USERS]: -1, // Unlimited
      [FEATURE_KEYS.MAX_PROJECTS]: -1, // Unlimited
      [FEATURE_KEYS.MAX_CUSTOM_FIELDS]: -1, // Unlimited custom fields
      [FEATURE_KEYS.MAX_TEMPLATES]: -1, // Unlimited templates
      [FEATURE_KEYS.MAX_FORMS]: -1, // Unlimited forms
      [FEATURE_KEYS.MAX_COLLECTIONS]: -1, // Unlimited collections
      [FEATURE_KEYS.MAX_LEADS]: -1, // Legacy migration limit
      [FEATURE_KEYS.MAX_DEALS]: -1, // Unlimited deals
      [FEATURE_KEYS.MAX_CONTACTS]: -1, // Unlimited contacts
      [FEATURE_KEYS.MAX_ENTITIES]: -1, // Unlimited entities
      [FEATURE_KEYS.STORAGE_GB]: 1000, // 1 TB (1000 GB)
      [FEATURE_KEYS.MARKETING_HOURLY_SENDS]: 5000,
      [FEATURE_KEYS.MARKETING_DAILY_SENDS]: 50000,

      // AI Features
      [FEATURE_KEYS.AI_CREDITS_MONTHLY]: 500,

      // Core Features
      [FEATURE_KEYS.PROJECTS]: true,
      [FEATURE_KEYS.TASKS_UNLIMITED]: true,
      [FEATURE_KEYS.TICKETS]: true,
      [FEATURE_KEYS.TIME_TRACKING]: true,
      [FEATURE_KEYS.DISCUSSIONS]: true,
      [FEATURE_KEYS.FORMS]: true, // Included in business

      // Entity Management
      [FEATURE_KEYS.ENTITY_PORTAL]: true,
      [FEATURE_KEYS.LEADS]: true,
      [FEATURE_KEYS.DEALS]: true,

      // Business Features
      [FEATURE_KEYS.EVENTS]: true,
      [FEATURE_KEYS.DIRECTORY]: true,
      [FEATURE_KEYS.TEAM]: true, // Included in business
      [FEATURE_KEYS.INVENTORY]: true,
      [FEATURE_KEYS.SUPPLIERS]: true, // Included in business
      [FEATURE_KEYS.CONTRACTS]: true, // Included in business

      // Advanced Features
      [FEATURE_KEYS.ADVANCED_REPORTS]: true,
      [FEATURE_KEYS.API_ACCESS]: true,
      [FEATURE_KEYS.WORKFLOWS]: true, // Included in business
      [FEATURE_KEYS.ANALYTICS]: true, // Included in business
      [FEATURE_KEYS.COLLECTIONS]: true, // Included in business
      [FEATURE_KEYS.INTEGRATIONS]: true, // Included in business
      [FEATURE_KEYS.DATA_EXPORT]: true, // CSV/Excel export included
      [FEATURE_KEYS.REMOVE_WATERMARK]: true, // No watermark on invoices/forms
      [FEATURE_KEYS.FORM_CAPTCHA]: true, // reCAPTCHA enabled for business
      [FEATURE_KEYS.FORM_KIOSK_MODE]: true, // Kiosk mode enabled for business

      // Support
      [FEATURE_KEYS.EMAIL_SUPPORT]: true,
      [FEATURE_KEYS.PRIORITY_SUPPORT]: true,
    },
  },
];

// Legacy export for backward compatibility
export const DEFAULT_SUBSCRIPTION_PLANS = SUBSCRIPTION_PLAN_CONFIGS.map(config => ({
  key: config.key,
  displayName: config.displayName,
  description: config.description,
  monthlyPrice: config.price,
  annualPrice: config.annualPrice,
  pricePerUser: config.pricePerUser,
  trialDays: config.trialPeriodDays,
  sortOrder: config.sortOrder,
  features: config.features,
}));
